import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ShipmentTableRowProps } from './ShipmentTable.props';
import { NodeTypeCode } from '../../../configs/enums';
import { formatFriendlyDateTime } from '../../../utils/common';
import { NotesCell } from '../../atoms/NotesCell';
import { TablePill, toneForStatus, type TablePillTone } from '../../atoms/TablePill';
import { ColumnDef } from '../StructureTable';
import { RowActionsMenu } from '../StructureTable/RowActionsMenu';

const CELL_SX = {
  fontSize: '11px',
  py: 0.25,
  px: 1,
  borderBottom: '1px solid',
  borderColor: 'v4.border',
} as const;

const INLINE_LINK_SX = {
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
} as const;

const MULTILINE_CELL_SX = {
  fontSize: '11px',
  whiteSpace: 'pre-line' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical' as const,
};

const DATE_FIELDS = new Set(['createdAt', 'updatedAt', 'completedAt', 'arrivalDate']);
const NUMBER_FIELDS = new Set(['qtyTotal', 'qtyReceived', 'qtyPending', 'shippingCost']);
const MULTILINE_FIELDS = new Set([
  'clientContractNames',
  'sourceName',
  'destinationName',
  'latestTrackingStatus',
]);

/** Shipment types carry no good/bad meaning, so they all read neutral except
 *  the two the warehouse treats as exceptions. */
const TYPE_CHIP_TONES: Record<string, TablePillTone> = {
  'add-stock': 'info',
  dispatch: 'positive',
  return: 'warning',
  'internal-transfer': 'neutral',
};

const TypeChip: React.FC<{ code: string; name: string }> = ({ code, name }) =>
  name ? <TablePill tone={TYPE_CHIP_TONES[code] ?? 'neutral'}>{name}</TablePill> : null;

// Through the shared mapper, so a shipment's "Cancelled" is the same colour as
// a job's. It used to be neutral here and red there.
const StatusPill: React.FC<{ code: string; name: string }> = ({ code, name }) =>
  name ? <TablePill tone={toneForStatus(code)}>{name}</TablePill> : null;

const renderDate = (value: unknown): string => {
  if (!value) return '';
  return formatFriendlyDateTime(String(value)) ?? '';
};

const renderNumber = (value: unknown): string => {
  if (value == null || value === '') return '';
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString() : String(value);
};

// Multiline fields whose value is a node name: maps the column key to the row
// fields holding that node's id and type, so the name can link to
// /structure/:id (and be gated by type).
const NODE_LINK_FIELDS: Record<string, { idField: string; typeField: string }> = {
  sourceName: { idField: 'sourceNodeId', typeField: 'sourceNodeType' },
  destinationName: { idField: 'destinationNodeId', typeField: 'destinationNodeType' },
};

// Sibling (user) nodes break /structure/:id — it expects address/contact
// properties they don't have — so they're never linked. The list view exposes
// the node-type display name ("Sibling"), which lower-cases to the same value
// as the NodeTypeCode.Sibling code, so this matches whether the field carries
// the name or the code.
const isSiblingNodeType = (nodeType: unknown): boolean =>
  String(nodeType ?? '')
    .trim()
    .toLowerCase() === NodeTypeCode.Sibling;

// Renders a node name as a link to its location page (/structure/:id) when the
// user can view structure locations, a node id is present, and the node isn't a
// sibling/user node; otherwise plain text. Mirrors the gating used elsewhere
// (ServiceTableRow, the Structure page Shipments tab). stopPropagation keeps
// row-level click handlers from firing.
const renderNodeLink = (
  text: string,
  nodeId: unknown,
  nodeType: unknown,
  canViewStructure: boolean,
  accent: string
): React.ReactNode => {
  const id = typeof nodeId === 'number' ? nodeId : Number(nodeId);
  if (
    !text ||
    !canViewStructure ||
    !Number.isFinite(id) ||
    id <= 0 ||
    isSiblingNodeType(nodeType)
  ) {
    return text;
  }
  return (
    <MuiLink
      component={RouterLink}
      to={`/structure/${id}`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{ ...INLINE_LINK_SX, color: accent, fontWeight: 500 }}
    >
      {text}
    </MuiLink>
  );
};

const renderMultilineCell = (
  col: ColumnDef,
  row: Record<string, unknown>,
  value: unknown,
  canViewStructure: boolean,
  accent: string
): React.ReactNode => {
  const text = value != null ? String(value) : '';
  const fields = NODE_LINK_FIELDS[col.key];
  const content = fields
    ? renderNodeLink(text, row[fields.idField], row[fields.typeField], canViewStructure, accent)
    : text;
  return (
    <Box sx={MULTILINE_CELL_SX} title={text}>
      {content}
    </Box>
  );
};

export const ShipmentTableRow: React.FC<ShipmentTableRowProps> = React.memo(
  ({ row, columns, even, detailHref, rowActions, onAction, canViewStructure }) => {
    const theme = useTheme();
    const shipmentId = row.id as number | undefined;
    const accent = theme.palette.v4.accent;

    const getRowBg = () => {
      if (even) return theme.palette.v4.tableEven;
      return 'background.paper';
    };

    const renderCell = (col: ColumnDef) => {
      if (col.render) return col.render(row);

      const value = row[col.key];

      if (col.key === 'id' && shipmentId != null) {
        return (
          <MuiLink
            component={RouterLink}
            to={detailHref}
            data-testid="shipment-detail-link"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ color: accent, fontWeight: 600, textDecoration: 'none' }}
          >
            {String(shipmentId)}
          </MuiLink>
        );
      }

      if (col.key === 'shipmentTypeCode') {
        const code = value != null ? String(value) : '';
        const name = (row.shipmentTypeName as string | undefined) ?? code;
        return <TypeChip code={code} name={name} />;
      }

      if (col.key === 'shipmentStatusCode') {
        const code = value != null ? String(value) : '';
        const name = (row.shipmentStatusName as string | undefined) ?? code;
        return <StatusPill code={code} name={name} />;
      }

      if (DATE_FIELDS.has(col.key)) {
        return renderDate(value);
      }

      if (NUMBER_FIELDS.has(col.key)) {
        return renderNumber(value);
      }

      if (col.key === 'notes') {
        return <NotesCell value={value} lineClamp={3} />;
      }

      if (MULTILINE_FIELDS.has(col.key)) {
        return renderMultilineCell(col, row, value, canViewStructure, accent);
      }

      return value != null ? String(value) : '';
    };

    return (
      <TableRow
        data-testid="shipment-table-row"
        sx={{
          bgcolor: getRowBg(),
          '&:hover': { bgcolor: theme.palette.v4.tableHover },
          transition: 'background-color 0.15s ease',
        }}
      >
        {columns.map((col) => (
          <TableCell
            key={col.key}
            sx={{ ...CELL_SX, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {renderCell(col)}
          </TableCell>
        ))}
        <TableCell
          sx={{
            ...CELL_SX,
            position: 'sticky',
            right: 0,
            bgcolor: 'inherit',
            zIndex: 1,
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}
          >
            <IconButton
              component={RouterLink}
              to={detailHref}
              size="small"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              aria-label={`Open shipment ${shipmentId ?? ''}`}
              sx={{ color: 'v4.accent' }}
            >
              <Box component="span" sx={{ fontSize: 14, fontWeight: 700 }}>
                &rarr;
              </Box>
            </IconButton>
            {rowActions.length > 0 && (
              <RowActionsMenu nodeId={shipmentId ?? 0} actions={rowActions} onAction={onAction} />
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  }
);

ShipmentTableRow.displayName = 'ShipmentTableRow';
